import { FormEvent, useEffect, useState } from "react";
import { Search } from "lucide-react";
import type { SandboxAuditFilterOptions } from "@/api/sandboxAudit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export type AuditFilterValues = {
  q: string;
  user_id: string;
  team_id: string;
  date_from: string;
  date_to: string;
};

export const emptyAuditFilters: AuditFilterValues = {
  q: "",
  user_id: "",
  team_id: "",
  date_from: "",
  date_to: "",
};

const ALL = "all";

type Props = {
  values: AuditFilterValues;
  options: SandboxAuditFilterOptions | null;
  onChange: (values: AuditFilterValues) => void;
};

export function AuditFilters({ values, options, onChange }: Props) {
  const [searchDraft, setSearchDraft] = useState(values.q);

  useEffect(() => {
    setSearchDraft(values.q);
  }, [values.q]);

  const update = (patch: Partial<AuditFilterValues>) => onChange({ ...values, ...patch });

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    update({ q: searchDraft.trim() });
  };

  const people = options?.people ?? [];
  const teams = options?.teams ?? [];
  const hasFilters = Object.values(values).some(Boolean);

  return (
    <div className="flex flex-wrap items-end gap-3">
      <form role="search" className="flex min-w-[16rem] flex-1 items-end gap-2" onSubmit={submitSearch}>
        <div className="grid flex-1 gap-1.5">
          <Label htmlFor="audit-search">Search titles, prompts, and responses</Label>
          <Input
            id="audit-search"
            placeholder="Words from a title, prompt, or response"
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
          />
        </div>
        <Button type="submit" variant="secondary">
          <Search className="mr-1.5 h-4 w-4" />
          Search
        </Button>
      </form>

      <div className="grid w-56 gap-1.5">
        <Label htmlFor="audit-person">Person</Label>
        <Select
          value={values.user_id || ALL}
          onValueChange={(next) => update({ user_id: next === ALL ? "" : next })}
        >
          <SelectTrigger id="audit-person" aria-label="Person">
            <SelectValue placeholder="Everyone" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Everyone</SelectItem>
            {people.map((person) => (
              <SelectItem key={person.id} value={String(person.id)}>
                {person.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {teams.length > 1 && (
        <div className="grid w-48 gap-1.5">
          <Label htmlFor="audit-team">Team</Label>
          <Select
            value={values.team_id || ALL}
            onValueChange={(next) => update({ team_id: next === ALL ? "" : next })}
          >
            <SelectTrigger id="audit-team" aria-label="Team">
              <SelectValue placeholder="All teams" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All teams</SelectItem>
              {teams.map((team) => (
                <SelectItem key={team.id} value={String(team.id)}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid gap-1.5">
        <Label htmlFor="audit-date-from">From</Label>
        <Input
          id="audit-date-from"
          type="date"
          className="w-40"
          value={values.date_from}
          onChange={(event) => update({ date_from: event.target.value })}
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="audit-date-to">To</Label>
        <Input
          id="audit-date-to"
          type="date"
          className="w-40"
          value={values.date_to}
          onChange={(event) => update({ date_to: event.target.value })}
        />
      </div>

      {hasFilters && (
        <Button type="button" variant="ghost" onClick={() => onChange(emptyAuditFilters)}>
          Clear filters
        </Button>
      )}
    </div>
  );
}

// Dates are whole days in the viewer's own time zone, the same zone the list
// shows times in. Send both bounds as absolute instants: the server runs in UTC,
// so a bare date would shift the day for anyone outside it.
export function auditFilterParams(values: AuditFilterValues) {
  const params = new URLSearchParams();
  if (values.q) params.set("q", values.q);
  if (values.user_id) params.set("user_id", values.user_id);
  if (values.team_id) params.set("team_id", values.team_id);
  const dateFrom = values.date_from && localDayBoundary(values.date_from, "start");
  const dateTo = values.date_to && localDayBoundary(values.date_to, "end");
  if (dateFrom) params.set("date_from", dateFrom);
  if (dateTo) params.set("date_to", dateTo);
  return params;
}

// Browsers without a native date picker fall back to a text input, so a
// half-typed day can arrive here. Skip it instead of throwing on toISOString.
export function localDayBoundary(day: string, edge: "start" | "end") {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const date = new Date(`${day}T${edge === "start" ? "00:00:00" : "23:59:59.999"}`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}
